import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

// Icons
import { Calendar, Clock, Plus, Users, BookOpen, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimetableManagement = ({ user }) => {
  const [timetables, setTimetables] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [view, setView] = useState('week'); // 'week' or 'my-schedule'
  
  const [formData, setFormData] = useState({
    class_id: '',
    subject_id: '',
    teacher_id: '',
    day_of_week: 0,
    start_time: '08:00',
    end_time: '09:00',
    room: ''
  });

  const days = [
    { value: 0, label: 'Lundi' },
    { value: 1, label: 'Mardi' },
    { value: 2, label: 'Mercredi' },
    { value: 3, label: 'Jeudi' },
    { value: 4, label: 'Vendredi' },
    { value: 5, label: 'Samedi' }
  ];

  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30', '18:00'
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (view === 'my-schedule') {
      fetchMySchedule();
    } else {
      fetchTimetables();
    }
  }, [selectedClass, view]);

  const fetchInitialData = async () => {
    try {
      const [classesRes, subjectsRes, teachersRes] = await Promise.all([
        axios.get(`${API}/classes`),
        axios.get(`${API}/subjects`),
        axios.get(`${API}/users?role=TEACHER`)
      ]);
      
      setClasses(classesRes.data);
      setSubjects(subjectsRes.data);
      setTeachers(teachersRes.data);
      
      // Set default class if available
      if (classesRes.data.length > 0) {
        setSelectedClass(classesRes.data[0].id);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchTimetables = async () => {
    try {
      const params = selectedClass ? { class_id: selectedClass } : {};
      const response = await axios.get(`${API}/timetables`, { params });
      setTimetables(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des emplois du temps');
      console.error('Error fetching timetables:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMySchedule = async () => {
    try {
      const response = await axios.get(`${API}/timetables/my-schedule`);
      setTimetables(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de votre emploi du temps');
      console.error('Error fetching my schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/timetables`, {
        ...formData,
        day_of_week: parseInt(formData.day_of_week)
      });
      
      toast.success('Créneau ajouté avec succès');
      resetForm();
      fetchTimetables();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la création du créneau');
      console.error('Error creating timetable:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      class_id: selectedClass || '',
      subject_id: '',
      teacher_id: '',
      day_of_week: 0,
      start_time: '08:00',
      end_time: '09:00',
      room: ''
    });
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Matière inconnue';
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Enseignant inconnu';
  };

  const getClassName = (classId) => {
    const classObj = classes.find(c => c.id === classId);
    return classObj ? classObj.name : 'Classe inconnue';
  };

  const getTimetableForDay = (dayIndex) => {
    return timetables
      .filter(t => t.day_of_week === dayIndex)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const canManageTimetables = user.role === 'ADMIN' || user.role === 'PRINCIPAL' || user.role === 'TEACHER';

  if (loading && timetables.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="timetable-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-blue-600" />
            Emplois du Temps
          </h1>
          <p className="text-gray-600 mt-1">
            Consultez et gérez les emplois du temps
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* View Toggle */}
          <div className="flex items-center space-x-2">
            <Button
              variant={view === 'week' ? 'default' : 'outline'}
              onClick={() => setView('week')}
              size="sm"
            >
              Vue Classe
            </Button>
            {(user.role === 'TEACHER' || user.role === 'STUDENT') && (
              <Button
                variant={view === 'my-schedule' ? 'default' : 'outline'}
                onClick={() => setView('my-schedule')}
                size="sm"
              >
                Mon Emploi du Temps
              </Button>
            )}
          </div>
          
          {canManageTimetables && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="add-timetable-btn"
                  onClick={() => { resetForm(); setDialogOpen(true); }}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Créneau
                </Button>
              </DialogTrigger>
              
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Nouveau créneau</DialogTitle>
                  <DialogDescription>
                    Ajoutez un nouveau cours à l'emploi du temps
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="class_id">Classe</Label>
                    <Select 
                      value={formData.class_id} 
                      onValueChange={(value) => setFormData({ ...formData, class_id: value })}
                    >
                      <SelectTrigger data-testid="timetable-class-select">
                        <SelectValue placeholder="Sélectionnez une classe" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((classObj) => (
                          <SelectItem key={classObj.id} value={classObj.id}>
                            {classObj.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subject_id">Matière</Label>
                    <Select 
                      value={formData.subject_id} 
                      onValueChange={(value) => setFormData({ ...formData, subject_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez une matière" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map((subject) => (
                          <SelectItem key={subject.id} value={subject.id}>
                            {subject.name} ({subject.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="teacher_id">Enseignant</Label>
                    <Select 
                      value={formData.teacher_id} 
                      onValueChange={(value) => setFormData({ ...formData, teacher_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un enseignant" />
                      </SelectTrigger>
                      <SelectContent>
                        {teachers.map((teacher) => (
                          <SelectItem key={teacher.id} value={teacher.id}>
                            {teacher.first_name} {teacher.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="day_of_week">Jour</Label>
                    <Select 
                      value={formData.day_of_week.toString()} 
                      onValueChange={(value) => setFormData({ ...formData, day_of_week: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un jour" />
                      </SelectTrigger>
                      <SelectContent>
                        {days.map((day) => (
                          <SelectItem key={day.value} value={day.value.toString()}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_time">Heure de début</Label>
                      <Select 
                        value={formData.start_time} 
                        onValueChange={(value) => setFormData({ ...formData, start_time: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="end_time">Heure de fin</Label>
                      <Select 
                        value={formData.end_time} 
                        onValueChange={(value) => setFormData({ ...formData, end_time: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="room">Salle (optionnel)</Label>
                    <Input
                      id="room"
                      value={formData.room}
                      onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                      placeholder="ex: Salle 101, Labo Sciences..."
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setDialogOpen(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="btn-primary"
                    >
                      {loading ? 'Création...' : 'Créer le créneau'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Class Selector for week view */}
      {view === 'week' && classes.length > 0 && (
        <Card className="card">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <Label>Classe sélectionnée:</Label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Choisir une classe" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((classObj) => (
                    <SelectItem key={classObj.id} value={classObj.id}>
                      {classObj.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timetable Grid */}
      <Card className="card">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            {view === 'my-schedule' ? 'Mon Emploi du Temps' : `Emploi du Temps - ${getClassName(selectedClass)}`}
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-6 gap-4">
            {days.map((day) => {
              const dayTimetables = getTimetableForDay(day.value);
              return (
                <div key={day.value} className="space-y-2">
                  <h3 className="font-semibold text-center py-2 bg-blue-50 rounded text-blue-800">
                    {day.label}
                  </h3>
                  
                  <div className="space-y-2">
                    {dayTimetables.length > 0 ? (
                      dayTimetables.map((timetable) => (
                        <Card key={timetable.id} className="p-3 border-l-4 border-l-blue-500">
                          <div className="space-y-1">
                            <div className="text-sm font-medium">
                              {getSubjectName(timetable.subject_id)}
                            </div>
                            <div className="text-xs text-gray-600 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {timetable.start_time} - {timetable.end_time}
                            </div>
                            <div className="text-xs text-gray-600 flex items-center">
                              <Users className="w-3 h-3 mr-1" />
                              {getTeacherName(timetable.teacher_id)}
                            </div>
                            {timetable.room && (
                              <div className="text-xs text-gray-600 flex items-center">
                                <MapPin className="w-3 h-3 mr-1" />
                                {timetable.room}
                              </div>
                            )}
                            {view === 'my-schedule' && (
                              <Badge className="badge-primary text-xs">
                                {getClassName(timetable.class_id)}
                              </Badge>
                            )}
                          </div>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">Aucun cours</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {timetables.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun emploi du temps</h3>
          <p className="text-gray-600 mb-6">
            {view === 'my-schedule' 
              ? 'Votre emploi du temps sera disponible une fois configuré par l\'administration.'
              : 'Commencez par créer des créneaux pour cette classe.'
            }
          </p>
          {canManageTimetables && view === 'week' && (
            <Button 
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer le premier créneau
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TimetableManagement;